/**
 * OWL2 Class Expression Types
 *
 * Defines all 18 class expression types from the OWL2 specification:
 * - Named class (Class)
 * - Boolean combinations: ObjectIntersectionOf, ObjectUnionOf, ObjectComplementOf, ObjectOneOf
 * - Object property restrictions: ObjectSomeValuesFrom, ObjectAllValuesFrom, ObjectHasValue,
 *   ObjectHasSelf, ObjectMinCardinality, ObjectMaxCardinality, ObjectExactCardinality
 * - Data property restrictions: DataSomeValuesFrom, DataAllValuesFrom, DataHasValue,
 *   DataMinCardinality, DataMaxCardinality, DataExactCardinality
 */
// =============================================================================
// Factory Functions
// =============================================================================
/**
 * Create a named class reference.
 */
export function owlClass(iri) {
    return { type: 'Class', iri };
}
/**
 * Create an intersection of class expressions.
 * Requires at least 2 operands.
 */
export function objectIntersectionOf(first, second, ...rest) {
    return {
        type: 'ObjectIntersectionOf',
        classExpressions: [first, second, ...rest]
    };
}
/**
 * Create a union of class expressions.
 * Requires at least 2 operands.
 */
export function objectUnionOf(first, second, ...rest) {
    return {
        type: 'ObjectUnionOf',
        classExpressions: [first, second, ...rest]
    };
}
/**
 * Create a complement of a class expression.
 */
export function objectComplementOf(classExpression) {
    return { type: 'ObjectComplementOf', classExpression };
}
/**
 * Create an enumeration of individuals.
 * Requires at least 1 individual.
 */
export function objectOneOf(first, ...rest) {
    return {
        type: 'ObjectOneOf',
        individuals: [first, ...rest]
    };
}
/**
 * Create an existential object property restriction.
 */
export function objectSomeValuesFrom(property, filler) {
    return { type: 'ObjectSomeValuesFrom', property, filler };
}
/**
 * Create a universal object property restriction.
 */
export function objectAllValuesFrom(property, filler) {
    return { type: 'ObjectAllValuesFrom', property, filler };
}
/**
 * Create a has-value object property restriction.
 */
export function objectHasValue(property, individual) {
    return { type: 'ObjectHasValue', property, individual };
}
/**
 * Create a has-self object property restriction.
 */
export function objectHasSelf(property) {
    return { type: 'ObjectHasSelf', property };
}
/**
 * Create a minimum cardinality object property restriction.
 */
export function objectMinCardinality(cardinality, property, filler) {
    return {
        type: 'ObjectMinCardinality',
        cardinality,
        property,
        ...(filler && { filler })
    };
}
/**
 * Create a maximum cardinality object property restriction.
 */
export function objectMaxCardinality(cardinality, property, filler) {
    return {
        type: 'ObjectMaxCardinality',
        cardinality,
        property,
        ...(filler && { filler })
    };
}
/**
 * Create an exact cardinality object property restriction.
 */
export function objectExactCardinality(cardinality, property, filler) {
    return {
        type: 'ObjectExactCardinality',
        cardinality,
        property,
        ...(filler && { filler })
    };
}
/**
 * Create an existential data property restriction.
 * Requires at least 1 property.
 */
export function dataSomeValuesFrom(properties, dataRange) {
    return { type: 'DataSomeValuesFrom', properties, dataRange };
}
/**
 * Create a universal data property restriction.
 * Requires at least 1 property.
 */
export function dataAllValuesFrom(properties, dataRange) {
    return { type: 'DataAllValuesFrom', properties, dataRange };
}
/**
 * Create a has-value data property restriction.
 */
export function dataHasValue(property, literal) {
    return { type: 'DataHasValue', property, literal };
}
/**
 * Create a minimum cardinality data property restriction.
 */
export function dataMinCardinality(cardinality, property, dataRange) {
    return {
        type: 'DataMinCardinality',
        cardinality,
        property,
        ...(dataRange && { dataRange })
    };
}
/**
 * Create a maximum cardinality data property restriction.
 */
export function dataMaxCardinality(cardinality, property, dataRange) {
    return {
        type: 'DataMaxCardinality',
        cardinality,
        property,
        ...(dataRange && { dataRange })
    };
}
/**
 * Create an exact cardinality data property restriction.
 */
export function dataExactCardinality(cardinality, property, dataRange) {
    return {
        type: 'DataExactCardinality',
        cardinality,
        property,
        ...(dataRange && { dataRange })
    };
}
// =============================================================================
// Type Guards
// =============================================================================
// Named class
export function isClass(ce) {
    return ce.type === 'Class';
}
// Boolean combinations
export function isObjectIntersectionOf(ce) {
    return ce.type === 'ObjectIntersectionOf';
}
export function isObjectUnionOf(ce) {
    return ce.type === 'ObjectUnionOf';
}
export function isObjectComplementOf(ce) {
    return ce.type === 'ObjectComplementOf';
}
export function isObjectOneOf(ce) {
    return ce.type === 'ObjectOneOf';
}
// Object property restrictions
export function isObjectSomeValuesFrom(ce) {
    return ce.type === 'ObjectSomeValuesFrom';
}
export function isObjectAllValuesFrom(ce) {
    return ce.type === 'ObjectAllValuesFrom';
}
export function isObjectHasValue(ce) {
    return ce.type === 'ObjectHasValue';
}
export function isObjectHasSelf(ce) {
    return ce.type === 'ObjectHasSelf';
}
export function isObjectMinCardinality(ce) {
    return ce.type === 'ObjectMinCardinality';
}
export function isObjectMaxCardinality(ce) {
    return ce.type === 'ObjectMaxCardinality';
}
export function isObjectExactCardinality(ce) {
    return ce.type === 'ObjectExactCardinality';
}
// Data property restrictions
export function isDataSomeValuesFrom(ce) {
    return ce.type === 'DataSomeValuesFrom';
}
export function isDataAllValuesFrom(ce) {
    return ce.type === 'DataAllValuesFrom';
}
export function isDataHasValue(ce) {
    return ce.type === 'DataHasValue';
}
export function isDataMinCardinality(ce) {
    return ce.type === 'DataMinCardinality';
}
export function isDataMaxCardinality(ce) {
    return ce.type === 'DataMaxCardinality';
}
export function isDataExactCardinality(ce) {
    return ce.type === 'DataExactCardinality';
}
// Aggregate type guards
export function isObjectPropertyRestriction(ce) {
    return (ce.type === 'ObjectSomeValuesFrom' ||
        ce.type === 'ObjectAllValuesFrom' ||
        ce.type === 'ObjectHasValue' ||
        ce.type === 'ObjectHasSelf' ||
        ce.type === 'ObjectMinCardinality' ||
        ce.type === 'ObjectMaxCardinality' ||
        ce.type === 'ObjectExactCardinality');
}
export function isDataPropertyRestriction(ce) {
    return (ce.type === 'DataSomeValuesFrom' ||
        ce.type === 'DataAllValuesFrom' ||
        ce.type === 'DataHasValue' ||
        ce.type === 'DataMinCardinality' ||
        ce.type === 'DataMaxCardinality' ||
        ce.type === 'DataExactCardinality');
}
export function isBooleanCombination(ce) {
    return (ce.type === 'ObjectIntersectionOf' ||
        ce.type === 'ObjectUnionOf' ||
        ce.type === 'ObjectComplementOf' ||
        ce.type === 'ObjectOneOf');
}
